import React, { useMemo, useState, useEffect, useCallback, useRef } from 'react';
import {
  FiPlay,
  FiX,
  FiClock,
  FiZap,
  FiTarget,
  FiAward,
  FiRotateCcw,
  FiCheckCircle,
  FiXCircle,
  FiVolume2,
  FiArrowRight,
  FiBarChart,
  FiTrendingUp,
  FiCalendar,
  FiHelpCircle,
} from 'react-icons/fi';
import { toastdisplay, speakWord } from '../utils/utils';
import { renderNuanceInlineMarkdown } from '../utils/renderNuanceInlineMarkdown';
import greWordsData from '../utils/greVocabularyData';
import './GREWordsChallenge.css';

const __DEV_CHALLENGE__ = process.env.NODE_ENV === 'development';
const devLog = (...args) => {
  if (__DEV_CHALLENGE__) console.log(...args);
};

// Game persistence utilities
const GAME_STORAGE_KEY = 'gre_words_challenge_data';

const wordSupportsRiddle = (w) =>
  !!(
    w?.question &&
    Array.isArray(w.options) &&
    w.options.length > 0 &&
    w.options.some(
      (opt) =>
        typeof opt === 'string' &&
        opt.toLowerCase() === (w.word || '').toLowerCase()
    )
  );

const roundModeLabel = (m) => {
  switch (m) {
    case 'riddle':
      return 'Riddle';
    case 'word-to-definition':
      return 'Word → Definition';
    case 'definition-to-word':
      return 'Definition → Word';
    case 'mixed':
      return 'Mixed';
    default:
      return m || '';
  }
};

const basePointsForDifficulty = (difficulty) => {
  const d = (difficulty || '').toLowerCase();
  if (d === 'easy') return 12;
  if (d === 'hard') return 22;
  return 16;
};

/** One vocabulary entry per headword (keeps session order sane). */
const dedupeGreWordsByKey = (words) => {
  if (!Array.isArray(words) || words.length === 0) return [];
  const seen = new Set();
  const out = [];
  for (const w of words) {
    const k = (w.word || '').toLowerCase();
    if (!k || seen.has(k)) continue;
    seen.add(k);
    out.push(w);
  }
  return out;
};

const getGameData = () => {
  try {
    const data = localStorage.getItem(GAME_STORAGE_KEY);
    return data
      ? JSON.parse(data)
      : {
          wordHistory: {}, // word -> { attempts: number, correct: number, lastPlayed: timestamp, difficulty: string }
          gameHistory: [], // Array of game sessions
          totalStats: {
            gamesPlayed: 0,
            totalWords: 0,
            totalCorrect: 0,
            bestStreak: 0,
            averageAccuracy: 0,
          },
        };
  } catch (error) {
    console.error('Error loading game data:', error);
    return {
      wordHistory: {},
      gameHistory: [],
      totalStats: {
        gamesPlayed: 0,
        totalWords: 0,
        totalCorrect: 0,
        bestStreak: 0,
        averageAccuracy: 0,
      },
    };
  }
};

const saveGameData = (gameData) => {
  try {
    localStorage.setItem(GAME_STORAGE_KEY, JSON.stringify(gameData));
  } catch (error) {
    console.error('Error saving game data:', error);
  }
};

const updateWordHistory = (word, isCorrect, difficulty) => {
  const gameData = getGameData();
  const wordKey = word.toLowerCase();

  if (!gameData.wordHistory[wordKey]) {
    gameData.wordHistory[wordKey] = {
      attempts: 0,
      correct: 0,
      lastPlayed: Date.now(),
      difficulty: difficulty || 'medium',
      word: word, // Store original word for display
    };
  }

  gameData.wordHistory[wordKey].attempts += 1;
  if (isCorrect) {
    gameData.wordHistory[wordKey].correct += 1;
  }
  gameData.wordHistory[wordKey].lastPlayed = Date.now();
  gameData.wordHistory[wordKey].difficulty =
    difficulty || gameData.wordHistory[wordKey].difficulty;

  saveGameData(gameData);
  return gameData;
};

const saveGameSession = (sessionData) => {
  const gameData = getGameData();

  // Add session to history
  gameData.gameHistory.unshift({
    ...sessionData,
    timestamp: Date.now(),
    date: new Date().toISOString().split('T')[0],
  });

  // Keep only last 50 sessions
  if (gameData.gameHistory.length > 50) {
    gameData.gameHistory = gameData.gameHistory.slice(0, 50);
  }

  // Update total stats
  gameData.totalStats.gamesPlayed += 1;
  gameData.totalStats.totalWords += sessionData.wordsPlayed;
  gameData.totalStats.totalCorrect += sessionData.correct;
  gameData.totalStats.bestStreak = Math.max(
    gameData.totalStats.bestStreak,
    sessionData.bestStreak
  );

  // Calculate average accuracy
  if (gameData.totalStats.totalWords > 0) {
    gameData.totalStats.averageAccuracy = Math.round(
      (gameData.totalStats.totalCorrect / gameData.totalStats.totalWords) * 100
    );
  }

  saveGameData(gameData);
};

const GREWordsChallenge = ({
  // Required props
  availableWords = [], // Array of word objects to play with
  onClose, // Function to close the game
  onSessionStarted = null,
  onSessionFinished = null,

  // Optional props with defaults
  showPremiumLimits = false, // Whether to show premium/free user limits
  maxFreeWords = 20, // Max words for free users (only used if showPremiumLimits is true)
  isUserPremium = false, // Whether user is premium (only used if showPremiumLimits is true)
  dailyWordsPlayed = 0, // Current daily words played (only used if showPremiumLimits is true)
  onWordsPlayedUpdate = null, // Callback when words are played (only used if showPremiumLimits is true)

  // Game initialization props (from VocabularyMastery)
  initialGameMode = 'riddle',
  initialWordCount = 10,
  initialTimeBetweenQuestions = 10,
  customWords = null, // Custom word set to play with
  autoStart = false, // Whether to start the game immediately
  hideRiddleMode = false, // Whether to hide riddle mode option (for flashcard book)
  showTextProgress = false, // Whether to show text progress instead of progress bar (for flashcard book)
  hideWordCountSelector = false, // Whether to hide word count selector (for flashcard book)
  wordPoolLabel = '', // Explains which words this session is drawn from

  // Word pools / grouping (optional)
  wordPoolOptions = null, // [{ key, label, description }]
  initialWordPoolKey = 'all',
  externalWordPools = null, // { [key]: words[] }
  initialLastAddedCount = 20,

  // Spaced repetition callback (optional) — called after each answer so the
  // parent can update SM-2 scheduling fields on the corresponding flashcard.
  onCardReviewed = null, // (word: string, isCorrect: boolean, quality: number) => void
}) => {
  // Game state
  const [gameActive, setGameActive] = useState(autoStart);
  const [gameSetupMode, setGameSetupMode] = useState(!autoStart);
  const [gameCompleted, setGameCompleted] = useState(false);
  const [showPerformanceStats, setShowPerformanceStats] = useState(false);

  // Current game data
  const [currentWord, setCurrentWord] = useState(null);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [showResult, setShowResult] = useState(false);

  // Game settings
  const [gameMode, setGameMode] = useState(
    hideRiddleMode && initialGameMode === 'riddle'
      ? 'word-to-definition'
      : initialGameMode
  );
  const [targetWordCount, setTargetWordCount] = useState(initialWordCount);

  useEffect(() => {
    const poolSize = Array.isArray(availableWords) ? availableWords.length : 0;
    if (!poolSize) return;
    setTargetWordCount((current) => {
      const next = Math.min(current || poolSize, poolSize);
      return next === current ? current : next;
    });
  }, [availableWords]);
  const [timeBetweenQuestions, setTimeBetweenQuestions] = useState(
    initialTimeBetweenQuestions
  );

  // Word pool settings (optional)
  const [wordPoolKey, setWordPoolKey] = useState(
    wordPoolOptions && Array.isArray(wordPoolOptions)
      ? initialWordPoolKey
      : 'all'
  );
  const [lastAddedCount, setLastAddedCount] = useState(initialLastAddedCount);

  const nuanceLookup = useMemo(() => {
    const map = {};
    greWordsData.forEach((w) => {
      if (w.usage_nuance_fr || w.usage_nuance_en) {
        map[w.word.toLowerCase()] = {
          usage_nuance_fr: w.usage_nuance_fr || '',
          usage_nuance_en: w.usage_nuance_en || '',
        };
      }
    });
    return map;
  }, []);

  const getNuanceForWord = useCallback(
    (word) => {
      if (!word) return null;
      const nuance = nuanceLookup[word.toLowerCase()];
      if (!nuance) return null;
      const combined = `${nuance.usage_nuance_fr || ''}\n${nuance.usage_nuance_en || ''}`;
      const isFalseFriend = /faux ami|false friend/i.test(combined);
      return { ...nuance, isFalseFriend };
    },
    [nuanceLookup]
  );

  // Game progress
  const [wordsPlayed, setWordsPlayed] = useState(0);
  const [gameScore, setGameScore] = useState(0);
  const [gameStats, setGameStats] = useState({
    correct: 0,
    incorrect: 0,
    streak: 0,
    bestStreak: 0,
  });
  // eslint-disable-next-line no-unused-vars
  const [masteredWords, setMasteredWords] = useState(new Set());

  const [sessionMissedWords, setSessionMissedWords] = useState([]);
  const [eliminatedOptions, setEliminatedOptions] = useState([]);
  const [showFirstLetterHint, setShowFirstLetterHint] = useState(false);
  const [hint5050Used, setHint5050Used] = useState(false);
  const [hintLetterUsed, setHintLetterUsed] = useState(false);

  // Timer states
  const [isTimedMode, setIsTimedMode] = useState(true);
  const [timeLeft, setTimeLeft] = useState(0);
  const [maxTime, setMaxTime] = useState(0);
  const [timerInterval, setTimerInterval] = useState(null);
  const [autoAdvanceTimeout, setAutoAdvanceTimeout] = useState(null);

  // Ref to track if timer is currently running (prevents multiple executions)
  const isTimerRunningRef = useRef(false);

  // Ref to track if word generation is currently running (prevents multiple executions)
  const isGeneratingWordRef = useRef(false);

  // Ref to track if wordsPlayed increment is pending (prevents multiple increments)
  const wordsPlayedIncrementRef = useRef(false);

  // Ref to track if timer start is pending (prevents multiple timer starts)
  const timerStartRef = useRef(false);

  const mixedRoundRef = useRef(0);
  /** Shuffled order for current cycle; reshuffled when a full cycle completes. */
  const sessionQueueRef = useRef([]);

  // Safe timer start function that prevents multiple timer starts
  const safeStartTimer = useCallback((difficulty) => {
    if (timerStartRef.current) {
      devLog(
        'Timer start already pending, preventing duplicate timer start'
      );
      return;
    }

    timerStartRef.current = true;
    devLog(`Starting timer for difficulty: ${difficulty}`);

    // Start the timer
    startTimer(difficulty);

    // Reset the flag after a short delay to allow the timer to initialize
    setTimeout(() => {
      timerStartRef.current = false;
    }, 100);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Safe increment function that prevents multiple increments
  const safeIncrementWordsPlayed = useCallback(() => {
    if (wordsPlayedIncrementRef.current) {
      devLog(
        'Words played increment already pending, preventing duplicate increment'
      );
      return;
    }

    wordsPlayedIncrementRef.current = true;
    setWordsPlayed((prev) => {
      const nextWordCount = prev + 1;
      devLog(`Incrementing words played from ${prev} to ${nextWordCount}`);

      // Reset the flag after state update
      setTimeout(() => {
        wordsPlayedIncrementRef.current = false;
      }, 0);

      return nextWordCount;
    });
  }, []);

  // Get words to use for the game
  const getWordsToUse = useCallback(() => {
    // If word pools are enabled, pool selection drives the word source.
    if (wordPoolOptions && Array.isArray(wordPoolOptions)) {
      const now = new Date();
      const base = Array.isArray(availableWords) ? availableWords : [];

      const getFromExternal = (key) => {
        if (!externalWordPools) return null;
        const pool = externalWordPools[key];
        return Array.isArray(pool) ? pool : null;
      };

      const poolKey = wordPoolKey || initialWordPoolKey || 'all';
      const fromExternal = getFromExternal(poolKey);
      if (fromExternal) return fromExternal;

      switch (poolKey) {
        case 'due':
          return base.filter((w) => {
            if (!w.nextReviewDate) return false;
            return new Date(w.nextReviewDate) <= now;
          });
        case 'thisWeek': {
          const last7Days = new Date(now);
          last7Days.setDate(last7Days.getDate() - 7);
          return base.filter((w) => {
            if (!w.addedAt) return false;
            return new Date(w.addedAt) >= last7Days;
          });
        }
        case 'lastAdded': {
          const sorted = [...base].sort((a, b) => {
            const aDate = a.addedAt ? new Date(a.addedAt) : new Date(0);
            const bDate = b.addedAt ? new Date(b.addedAt) : new Date(0);
            return bDate - aDate;
          });
          return sorted.slice(0, Math.max(1, lastAddedCount || 20));
        }
        case 'new':
          return base.filter((w) => (w.reviewCount ?? 0) === 0);
        case 'learning':
          return base.filter((w) => {
            const count = w.reviewCount ?? 0;
            return count > 0 && count < 5;
          });
        case 'mastered':
          return base.filter((w) => (w.reviewCount ?? 0) >= 5 && (w.easeFactor ?? 0) >= 2.5);
        case 'filtered': {
          const filtered = getFromExternal('filtered');
          return filtered || [];
        }
        case 'all':
        default:
          return base;
      }
    }

    // Backward-compatible default behavior
    if (customWords && customWords.length > 0) return customWords;
    return availableWords;
  }, [
    availableWords,
    customWords,
    externalWordPools,
    initialWordPoolKey,
    lastAddedCount,
    wordPoolKey,
    wordPoolOptions,
  ]);

  const poolCounts = useMemo(() => {
    if (!wordPoolOptions || !Array.isArray(wordPoolOptions)) return null;
    const counts = {};
    const now = new Date();
    const base = Array.isArray(availableWords) ? availableWords : [];

    const compute = (key) => {
      if (externalWordPools && Array.isArray(externalWordPools[key])) {
        return externalWordPools[key].length;
      }
      switch (key) {
        case 'due':
          return base.filter((w) => w.nextReviewDate && new Date(w.nextReviewDate) <= now).length;
        case 'thisWeek': {
          const last7Days = new Date(now);
          last7Days.setDate(last7Days.getDate() - 7);
          return base.filter((w) => w.addedAt && new Date(w.addedAt) >= last7Days).length;
        }
        case 'lastAdded':
          return Math.min(base.length, Math.max(1, lastAddedCount || 20));
        case 'new':
          return base.filter((w) => (w.reviewCount ?? 0) === 0).length;
        case 'learning':
          return base.filter((w) => {
            const count = w.reviewCount ?? 0;
            return count > 0 && count < 5;
          }).length;
        case 'mastered':
          return base.filter((w) => (w.reviewCount ?? 0) >= 5 && (w.easeFactor ?? 0) >= 2.5).length;
        case 'filtered':
          return externalWordPools && Array.isArray(externalWordPools.filtered)
            ? externalWordPools.filtered.length
            : 0;
        case 'all':
        default:
          return base.length;
      }
    };

    wordPoolOptions.forEach((opt) => {
      counts[opt.key] = compute(opt.key);
    });
    return counts;
  }, [availableWords, externalWordPools, lastAddedCount, wordPoolOptions]);

  // Proper Fisher-Yates shuffle algorithm
  const shuffleArray = useCallback((array) => {
    const shuffled = [...array]; // Create a copy to avoid mutating original
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  }, []);

  // Smart word filtering based on performance history
  const getSmartFilteredWords = useCallback(() => {
    const wordsToUse = getWordsToUse();
    if (!wordsToUse || wordsToUse.length === 0) return [];

    const gameData = getGameData();
    const wordHistory = gameData.wordHistory;

    // Categorize words
    const newWords = [];
    const incorrectWords = [];
    const reviewWords = [];

    wordsToUse.forEach((word) => {
      const wordKey = word.word.toLowerCase();
      const history = wordHistory[wordKey];

      if (!history) {
        // Never played before
        newWords.push(word);
      } else {
        const accuracy =
          history.attempts > 0 ? history.correct / history.attempts : 0;
        const daysSinceLastPlayed =
          (Date.now() - history.lastPlayed) / (1000 * 60 * 60 * 24);

        if (accuracy < 0.7) {
          // Less than 70% accuracy - needs practice
          incorrectWords.push({
            ...word,
            priority: 3,
            accuracy,
            daysSinceLastPlayed,
          });
        } else if (daysSinceLastPlayed > 7) {
          // Haven't seen in a week - review
          reviewWords.push({
            ...word,
            priority: 2,
            accuracy,
            daysSinceLastPlayed,
          });
        } else {
          // Well-known words - lower priority
          reviewWords.push({
            ...word,
            priority: 1,
            accuracy,
            daysSinceLastPlayed,
          });
        }
      }
    });

    // Sort by priority (incorrect words first, then new words, then review words)
    const prioritizedWords = [
      ...shuffleArray(incorrectWords.sort((a, b) => b.priority - a.priority)),
      ...shuffleArray(newWords),
      ...shuffleArray(
        reviewWords.sort(
          (a, b) => b.daysSinceLastPlayed - a.daysSinceLastPlayed
        )
      ),
    ];

    return prioritizedWords;
  }, [getWordsToUse, shuffleArray]);

  // Check if user can play more words (only for Community usage with premium limits)
  const canPlayWords = () => {
    if (!showPremiumLimits) return true;
    if (isUserPremium) return true;
    return dailyWordsPlayed < maxFreeWords;
  };

  // Get remaining words for free users
  const getRemainingWords = () => {
    if (!showPremiumLimits || isUserPremium) return 999;
    return Math.max(0, maxFreeWords - dailyWordsPlayed);
  };

  // Get time for difficulty level (matching Community.js standard times)
  const getTimeForDifficulty = (difficulty) => {
    switch (difficulty?.toLowerCase()) {
      case 'easy':
        return 10;
      case 'medium':
        return 20;
      case 'hard':
        return 30;
      default:
        return 20;
    }
  };

  // Start timer for timed mode
  const startTimer = (difficulty) => {
    // Prevent multiple timer executions
    if (isTimerRunningRef.current) {
      devLog('Timer already running, preventing duplicate execution');
      return;
    }

    // CRITICAL: Always clear any existing timer first
    if (timerInterval) {
      devLog('Clearing existing timer:', timerInterval);
      clearInterval(timerInterval);
      setTimerInterval(null);
    }

    // Mark timer as running
    isTimerRunningRef.current = true;

    const timeForDifficulty = getTimeForDifficulty(difficulty);
    setTimeLeft(timeForDifficulty);
    setMaxTime(timeForDifficulty);

    const interval = setInterval(() => {
      setTimeLeft((prevTime) => {
        if (prevTime <= 1) {
          // Clear this specific interval immediately
          clearInterval(interval);
          setTimerInterval(null);
          isTimerRunningRef.current = false; // Mark timer as stopped

          devLog('Timer expired, cleared interval:', interval);

          // Time's up - auto select wrong answer and move to next
          setSelectedAnswer('TIME_UP');
          setShowResult(true);

          // Save word performance as incorrect (timeout)
          if (currentWord) {
            updateWordHistory(currentWord.word, false, currentWord.difficulty);
            if (onCardReviewed) {
              onCardReviewed(currentWord.word, false, 0);
            }
            setSessionMissedWords((prev) =>
              prev.includes(currentWord.word)
                ? prev
                : [...prev, currentWord.word]
            );
          }

          // Update stats for incorrect answer
          setGameStats((prev) => ({
            ...prev,
            incorrect: prev.incorrect + 1,
            streak: 0,
          }));

          // Update daily words played count (only for Community with premium limits)
          if (showPremiumLimits && onWordsPlayedUpdate) {
            onWordsPlayedUpdate();
          }

          // Auto advance to next word after showing result
          const timeout = setTimeout(() => {
            advanceToNextQuestion();
          }, timeBetweenQuestions * 1000);

          setAutoAdvanceTimeout(timeout);

          return 0;
        }
        return prevTime - 1;
      });
    }, 1000);

    devLog('Setting new timer interval:', interval);
    setTimerInterval(interval);
  };

  // Clear timer and auto-advance timeout
  const clearTimer = () => {
    devLog('clearTimer called - current interval:', timerInterval);
    if (timerInterval) {
      clearInterval(timerInterval);
      setTimerInterval(null);
      devLog('Timer interval cleared');
    }
    if (autoAdvanceTimeout) {
      clearTimeout(autoAdvanceTimeout);
      setAutoAdvanceTimeout(null);
      devLog('Auto-advance timeout cleared');
    }
    // Reset timer and word generation flags
    isTimerRunningRef.current = false;
    isGeneratingWordRef.current = false;
    wordsPlayedIncrementRef.current = false;
    timerStartRef.current = false;
    setTimeLeft(0);
    setMaxTime(0);
  };

  // Generate new word from available words
  const generateNewWord = () => {
    // Prevent multiple word generation executions
    if (isGeneratingWordRef.current) {
      devLog(
        'Word generation already running, preventing duplicate execution'
      );
      return;
    }

    // Mark word generation as running
    isGeneratingWordRef.current = true;

    try {
      const poolForSession = dedupeGreWordsByKey(
        (!wordPoolOptions || !Array.isArray(wordPoolOptions)) &&
          customWords &&
          customWords.length > 0
          ? customWords
          : getSmartFilteredWords()
      );

      if (!poolForSession.length) {
        console.error('No words available for the game');
        return;
      }

      const len = poolForSession.length;
      if (
        !sessionQueueRef.current.length ||
        (wordsPlayed > 0 && wordsPlayed % len === 0)
      ) {
        sessionQueueRef.current = shuffleArray(poolForSession);
      }

      const randomWord = sessionQueueRef.current[wordsPlayed % len];

      let playMode =
        gameMode === 'mixed'
          ? (() => {
              const modes =
                !hideRiddleMode && wordSupportsRiddle(randomWord)
                  ? ['riddle', 'word-to-definition', 'definition-to-word']
                  : ['word-to-definition', 'definition-to-word'];
              const idx = mixedRoundRef.current % modes.length;
              mixedRoundRef.current += 1;
              return modes[idx];
            })()
          : gameMode;

      if (playMode === 'riddle' && !wordSupportsRiddle(randomWord)) {
        playMode = 'definition-to-word';
      }

      let questionData = { ...randomWord, roundMode: playMode };

      if (playMode === 'riddle') {
        questionData.question = randomWord.question;
        questionData.options = shuffleArray([...(randomWord.options || [])]);
      } else if (playMode === 'word-to-definition') {
        questionData.question = `What does "${randomWord.word}" mean?`;
        const allWords = getWordsToUse();
        const availableDefinitions = allWords
          .filter((w) => w.word !== randomWord.word)
          .map((w) => w.definition_en || w.definition);

        const wrongDefinitions = shuffleArray(availableDefinitions).slice(0, 3);

        const allOptions = [
          randomWord.definition_en || randomWord.definition,
          ...wrongDefinitions,
        ];
        questionData.options = shuffleArray(allOptions);
      } else if (playMode === 'definition-to-word') {
        questionData.question = `Which word means: "${randomWord.definition_en || randomWord.definition}"?`;
        const allWords = getWordsToUse();
        const availableWords = allWords
          .filter((w) => w.word !== randomWord.word)
          .map((w) => w.word);

        const wrongWords = shuffleArray(availableWords).slice(0, 3);

        const allOptions = [randomWord.word, ...wrongWords];
        questionData.options = shuffleArray(allOptions);
      }

      const riddleCorrect =
        Array.isArray(randomWord.options) && randomWord.options.length > 0
          ? randomWord.options.find(
              (opt) =>
                typeof opt === 'string' &&
                opt.toLowerCase() === randomWord.word.toLowerCase()
            ) ?? randomWord.options[0]
          : randomWord.word;

      const correctAnswer =
        playMode === 'riddle'
          ? riddleCorrect
          : playMode === 'word-to-definition'
            ? randomWord.definition_en || randomWord.definition
            : playMode === 'definition-to-word'
              ? randomWord.word
              : riddleCorrect;

      questionData.correctAnswer = correctAnswer;

      setEliminatedOptions([]);
      setShowFirstLetterHint(false);
      setHint5050Used(false);
      setHintLetterUsed(false);
      setCurrentWord(questionData);
      setSelectedAnswer(null);
      setShowResult(false);

      // Increment words played for the current question (safe increment)
      safeIncrementWordsPlayed();

      // Start timer if in timed mode (safe timer start)
      if (isTimedMode) {
        safeStartTimer(randomWord.difficulty);
      }
    } catch (error) {
      console.error('Error generating new word:', error);
    } finally {
      // Always reset word generation flag, even if there was an error
      isGeneratingWordRef.current = false;
    }
  };

  // Helper function to save current game session
  const saveCurrentGameSession = useCallback(
    (isCompleted = false) => {
      // Only save if we've played at least one word
      if (wordsPlayed > 0) {
        const sessionData = {
          gameMode,
          wordsPlayed,
          targetWordCount,
          correct: gameStats.correct,
          incorrect: gameStats.incorrect,
          bestStreak: gameStats.bestStreak,
          finalScore: gameScore,
          isTimedMode,
          timeBetweenQuestions,
          isCompleted,
          accuracy:
            gameStats.correct + gameStats.incorrect > 0
              ? Math.round(
                  (gameStats.correct /
                    (gameStats.correct + gameStats.incorrect)) *
                    100
                )
              : 0,
        };

        saveGameSession(sessionData);
      }
    },
    [
      gameMode,
      wordsPlayed,
      targetWordCount,
      gameStats,
      gameScore,
      isTimedMode,
      timeBetweenQuestions,
    ]
  );

  // Advance to next question
  const advanceToNextQuestion = () => {
    // Clear any existing auto-advance timeout
    if (autoAdvanceTimeout) {
      clearTimeout(autoAdvanceTimeout);
      setAutoAdvanceTimeout(null);
    }

    // Check if we've completed the target number of words
    if (wordsPlayed >= targetWordCount) {
      // Game completed - save session data
      saveCurrentGameSession(true);
      setGameCompleted(true);
      if (onSessionFinished) onSessionFinished();
      clearTimer();
    } else {
      generateNewWord();
    }
  };

  // Handle answer selection
  const handleAnswerSelect = (answer) => {
    if (showResult) return; // Prevent multiple selections

    setSelectedAnswer(answer);
    setShowResult(true);

    // Clear timer when answer is selected
    if (isTimedMode) {
      clearTimer();
    }

    const isCorrect = answer === currentWord.correctAnswer;
    const usedHint = hint5050Used || hintLetterUsed;

    // Save word performance to localStorage
    updateWordHistory(currentWord.word, isCorrect, currentWord.difficulty);

    if (isCorrect) {
      let pts = basePointsForDifficulty(currentWord.difficulty);
      if (usedHint) pts = Math.ceil(pts * 0.72);
      pts += Math.min(10, gameStats.streak * 2);
      setGameScore((prev) => prev + pts);
      setGameStats((prev) => ({
        ...prev,
        correct: prev.correct + 1,
        streak: prev.streak + 1,
        bestStreak: Math.max(prev.bestStreak, prev.streak + 1),
      }));

      // Mark word as mastered after 3 correct answers
      if (gameStats.streak >= 2) {
        setMasteredWords((prev) => new Set([...prev, currentWord.word]));
      }
    } else {
      setGameStats((prev) => ({
        ...prev,
        incorrect: prev.incorrect + 1,
        streak: 0,
      }));
      setSessionMissedWords((prev) =>
        currentWord.word && !prev.includes(currentWord.word)
          ? [...prev, currentWord.word]
          : prev
      );
    }

    // Notify parent for SM-2 spaced repetition update
    if (onCardReviewed) {
      let quality = isCorrect ? 4 : 1;
      if (isCorrect && usedHint) quality = 3;
      onCardReviewed(currentWord.word, isCorrect, quality);
    }

    // Update daily words played count (only for Community with premium limits)
    if (showPremiumLimits && onWordsPlayedUpdate) {
      onWordsPlayedUpdate();
    }

    // Auto advance to next word after showing result
    const timeout = setTimeout(() => {
      advanceToNextQuestion();
    }, timeBetweenQuestions * 1000);

    setAutoAdvanceTimeout(timeout);
  };

  // Launch game
  const launchGame = () => {
    if (!canPlayWords()) {
      toastdisplay(
        `Daily limit reached! You've played ${dailyWordsPlayed}/${maxFreeWords} words today. Upgrade to Premium for unlimited access.`,
        false,
        5000
      );
      return;
    }

    setGameSetupMode(false);
    setGameActive(true);
    if (onSessionStarted) onSessionStarted();
    wordsPlayedIncrementRef.current = false; // Reset increment flag
    timerStartRef.current = false; // Reset timer start flag
    setSessionMissedWords([]);
    mixedRoundRef.current = 0;
    sessionQueueRef.current = [];
    generateNewWord();
  };

  // Reset game
  const resetGame = () => {
    setGameScore(0);
    setGameStats({
      correct: 0,
      incorrect: 0,
      streak: 0,
      bestStreak: 0,
    });
    setMasteredWords(new Set());
    setWordsPlayed(0);
    setGameCompleted(false);
    setGameSetupMode(true);
    setSessionMissedWords([]);
    mixedRoundRef.current = 0;
    sessionQueueRef.current = [];
    clearTimer();
  };

  // Exit game
  const exitGame = () => {
    // Save current session data before exiting
    saveCurrentGameSession(false);
    setGameCompleted(true);
    clearTimer();
  };

  // Close game completely
  const closeGame = () => {
    // Save current session data if game is active and not completed
    if (gameActive && !gameCompleted) {
      saveCurrentGameSession(false);
    }
    clearTimer();
    if (onClose) onClose();
  };

  // Toggle timed mode
  const toggleTimedMode = () => {
    setIsTimedMode(!isTimedMode);
    if (!isTimedMode) {
      clearTimer();
    }
  };

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      clearTimer();
      isTimerRunningRef.current = false; // Reset ref on unmount
      isGeneratingWordRef.current = false; // Reset ref on unmount
      wordsPlayedIncrementRef.current = false; // Reset ref on unmount
      timerStartRef.current = false; // Reset ref on unmount
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-start game if specified
  useEffect(() => {
    if (autoStart && !gameActive && !gameCompleted) {
      launchGame();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoStart, gameActive, gameCompleted]);

  // Regenerate word when game mode changes
  useEffect(() => {
    if (gameActive && currentWord && !gameSetupMode) {
      generateNewWord();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gameMode]);

  // Clamp word count when pool changes
  useEffect(() => {
    if (!wordPoolOptions || !Array.isArray(wordPoolOptions)) return;
    const poolSize = getWordsToUse().length;
    if (poolSize <= 0) return;
    const minValue = Math.min(5, Math.max(1, poolSize));
    setTargetWordCount((prev) =>
      Math.min(Math.max(prev, minValue), poolSize)
    );
  }, [getWordsToUse, wordPoolKey, lastAddedCount, wordPoolOptions]);

  const [nuanceLanguage, setNuanceLanguage] = useState(() => {
    try {
      const saved = localStorage.getItem('gre_words_challenge_nuance_language');
      return ['both', 'fr', 'en'].includes(saved) ? saved : 'both';
    } catch {
      return 'both';
    }
  });

  const handleNuanceLanguageChange = useCallback((language) => {
    setNuanceLanguage(language);
    try {
      localStorage.setItem('gre_words_challenge_nuance_language', language);
    } catch {
      // Non-critical preference; ignore storage failures.
    }
  }, []);

  const displayOptions = useMemo(() => {
    if (!currentWord?.options) return [];
    const elim = new Set(eliminatedOptions);
    return currentWord.options.filter((o) => !elim.has(o));
  }, [currentWord?.options, eliminatedOptions]);

  const applyFiftyFifty = useCallback(() => {
    if (showResult || !currentWord?.options || hint5050Used) return;
    const wrong = currentWord.options.filter(
      (o) => o !== currentWord.correctAnswer
    );
    if (wrong.length < 2) return;
    const pick = shuffleArray(wrong).slice(0, 2);
    setEliminatedOptions(pick);
    setHint5050Used(true);
  }, [showResult, currentWord, hint5050Used, shuffleArray]);

  const applyFirstLetterHint = useCallback(() => {
    if (showResult || hintLetterUsed || !currentWord?.correctAnswer) return;
    setShowFirstLetterHint(true);
    setHintLetterUsed(true);
  }, [showResult, hintLetterUsed, currentWord?.correctAnswer]);

  useEffect(() => {
    if (!gameActive || gameSetupMode || gameCompleted || !currentWord || showResult)
      return;
    const onKey = (e) => {
      const t = e.target;
      if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable))
        return;
      const n = parseInt(e.key, 10);
      if (n >= 1 && n <= 4 && displayOptions[n - 1] !== undefined) {
        handleAnswerSelect(displayOptions[n - 1]);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    gameActive,
    gameSetupMode,
    gameCompleted,
    currentWord,
    showResult,
    displayOptions,
  ]);

  const firstLetterHintText = useMemo(() => {
    if (!showFirstLetterHint || !currentWord?.correctAnswer) return null;
    const ans = String(currentWord.correctAnswer);
    const ch = ans.trim().charAt(0);
    if (!ch) return null;
    return ch.toUpperCase();
  }, [showFirstLetterHint, currentWord?.correctAnswer]);

  const gameModeSetupHint = useMemo(() => {
    const hintKeys =
      'Use keys 1–4 to choose an option. Hints reduce points slightly.';
    if (gameMode === 'mixed') {
      return `Each question rotates: Riddle first when quiz data exists${
        hideRiddleMode ? ' (riddle is hidden on this screen)' : ''
      }, then Word → Definition, then Definition → Word. ${hintKeys}`;
    }
    if (gameMode === 'riddle') {
      return `Choose the word that matches the clue. ${hintKeys}`;
    }
    if (gameMode === 'word-to-definition') {
      return `You see the vocabulary word; pick the correct meaning. ${hintKeys}`;
    }
    if (gameMode === 'definition-to-word') {
      return `You see a definition; pick the matching word. ${hintKeys}`;
    }
    return hintKeys;
  }, [gameMode, hideRiddleMode]);

  return (
    <div className="gre-words-challenge-overlay">
      <div className="gre-words-challenge-modal">
        <div className="gre-words-challenge-header">
          <h2 className="gre-words-challenge-title">
            <FiTarget /> GRE Words Challenge
          </h2>
          <button
            onClick={closeGame}
            className="gre-words-challenge-close"
            title="Close game"
          >
            <FiX size={20} />
          </button>
        </div>

        {gameSetupMode && !showPerformanceStats && (
          <div className="gre-words-challenge-setup">
            <div className="gre-words-challenge-setup-header">
              <button
                onClick={() => setShowPerformanceStats(true)}
                className="gre-words-challenge-stats-btn"
                title="View your performance statistics"
              >
                <FiBarChart size={16} />
                View Stats
              </button>
            </div>

            {wordPoolOptions && Array.isArray(wordPoolOptions) && (
              <div className="gre-words-challenge-setup-section">
                <label className="gre-words-challenge-setup-label">
                  Word Pool:
                </label>
                <div className="gre-words-challenge-setup-control">
                  <select
                    value={wordPoolKey}
                    onChange={(e) => setWordPoolKey(e.target.value)}
                    className="gre-words-challenge-setup-select"
                  >
                    {wordPoolOptions.map((opt) => (
                      <option key={opt.key} value={opt.key}>
                        {opt.label}
                        {poolCounts && typeof poolCounts[opt.key] === 'number'
                          ? ` (${poolCounts[opt.key]})`
                          : ''}
                      </option>
                    ))}
                  </select>
                  {(() => {
                    const selected = wordPoolOptions.find((o) => o.key === wordPoolKey);
                    if (!selected?.description) return null;
                    return (
                      <div className="gre-words-challenge-pool-description">
                        {selected.description}
                      </div>
                    );
                  })()}
                </div>
              </div>
            )}

            {wordPoolOptions &&
              Array.isArray(wordPoolOptions) &&
              wordPoolKey === 'lastAdded' && (
                <div className="gre-words-challenge-setup-section">
                  <label className="gre-words-challenge-setup-label">
                    Last added count:
                  </label>
                  <div className="gre-words-challenge-setup-control">
                    <input
                      type="range"
                      min={1}
                      max={Math.max(1, Math.min(100, (availableWords || []).length || 1))}
                      value={Math.max(
                        1,
                        Math.min(
                          lastAddedCount,
                          Math.max(1, Math.min(100, (availableWords || []).length || 1))
                        )
                      )}
                      onChange={(e) => setLastAddedCount(parseInt(e.target.value))}
                      className="gre-words-challenge-setup-range"
                    />
                    <span className="gre-words-challenge-range-value">
                      {lastAddedCount}
                    </span>
                  </div>
                </div>
              )}

            <div className="gre-words-challenge-setup-section">
              <label className="gre-words-challenge-setup-label">
                Game Mode:
              </label>
              <div className="gre-words-challenge-setup-control">
                <select
                  value={gameMode}
                  onChange={(e) => setGameMode(e.target.value)}
                  className="gre-words-challenge-setup-select"
                >
                  {!hideRiddleMode && (
                    <option value="riddle">Riddle Mode</option>
                  )}
                  <option value="word-to-definition">Word → Definition</option>
                  <option value="definition-to-word">Definition → Word</option>
                  <option value="mixed">Mixed (rotates modes each question)</option>
                </select>
              </div>
              <p className="gre-words-challenge-mode-hint">{gameModeSetupHint}</p>
              {wordPoolLabel ? (
                <p className="gre-words-challenge-mode-hint">{wordPoolLabel}</p>
              ) : null}
            </div>

            {!hideWordCountSelector && getSmartFilteredWords().length > 0 && (
              <div className="gre-words-challenge-setup-section">
                <label className="gre-words-challenge-setup-label">
                  Number of Words:
                </label>
                <div className="gre-words-challenge-setup-control">
                  {(() => {
                    const maxWords = showPremiumLimits
                      ? isUserPremium
                        ? Math.min(100, getSmartFilteredWords().length)
                        : Math.min(50, getRemainingWords() || 0)
                      : Math.min(100, getSmartFilteredWords().length);

                    const safeValue = Math.min(
                      targetWordCount,
                      showPremiumLimits
                        ? isUserPremium
                          ? 100
                          : getRemainingWords() || 0
                        : getSmartFilteredWords().length
                    );

                    return (
                      <>
                        <input
                          type="range"
                          min={Math.min(5, Math.max(1, maxWords))}
                          max={maxWords}
                          value={Math.max(
                            Math.min(5, Math.max(1, maxWords)),
                            Math.min(safeValue, maxWords)
                          )}
                          onChange={(e) => {
                            const newCount = parseInt(e.target.value);
                            setTargetWordCount(newCount);
                          }}
                          className="gre-words-challenge-setup-range"
                        />
                        <span className="gre-words-challenge-range-value">
                          {targetWordCount}
                        </span>
                      </>
                    );
                  })()}
                </div>
              </div>
            )}

            <div className="gre-words-challenge-setup-section">
              <label className="gre-words-challenge-setup-label">
                Time to Read Answer Explanation:
              </label>
              <div className="gre-words-challenge-setup-control">
                <select
                  value={timeBetweenQuestions}
                  onChange={(e) =>
                    setTimeBetweenQuestions(parseInt(e.target.value))
                  }
                  className="gre-words-challenge-setup-select"
                >
                  <option value={3}>3 seconds</option>
                  <option value={5}>5 seconds</option>
                  <option value={10}>10 seconds</option>
                  <option value={15}>15 seconds</option>
                </select>
              </div>
            </div>

            <div className="gre-words-challenge-setup-section">
              <label className="gre-words-challenge-setup-checkbox-label">
                <input
                  type="checkbox"
                  checked={isTimedMode}
                  onChange={toggleTimedMode}
                  className="gre-words-challenge-setup-checkbox"
                />
                <span className="gre-words-challenge-setup-checkbox-text">
                  Timed Mode (adds pressure!)
                </span>
              </label>
            </div>

            {isTimedMode && (
              <div className="gre-words-challenge-timing-info">
                <div className="gre-words-challenge-timing-info-header">
                  <FiClock size={16} />
                  <span>Question Timing</span>
                </div>
                <div className="gre-words-challenge-timing-info-details">
                  <div className="gre-words-challenge-timing-detail">
                    <span className="gre-words-challenge-difficulty-badge gre-words-challenge-difficulty-easy">
                      EASY
                    </span>
                    <span>10 seconds</span>
                  </div>
                  <div className="gre-words-challenge-timing-detail">
                    <span className="gre-words-challenge-difficulty-badge gre-words-challenge-difficulty-medium">
                      MEDIUM
                    </span>
                    <span>20 seconds</span>
                  </div>
                  <div className="gre-words-challenge-timing-detail">
                    <span className="gre-words-challenge-difficulty-badge gre-words-challenge-difficulty-hard">
                      HARD
                    </span>
                    <span>30 seconds</span>
                  </div>
                </div>
              </div>
            )}

            {showPremiumLimits && !isUserPremium && (
              <div className="gre-words-challenge-premium-limit-warning">
                <FiZap size={16} />
                <span>
                  Free users: {getRemainingWords()}/{maxFreeWords} words
                  remaining today
                </span>
              </div>
            )}

            <div className="gre-words-challenge-setup-actions">
              <button
                className="gre-words-challenge-launch-game-btn"
                onClick={launchGame}
                disabled={
                  !canPlayWords() || getSmartFilteredWords().length === 0
                }
              >
                <FiPlay size={16} />
                {!canPlayWords()
                  ? 'Daily Limit Reached'
                  : getSmartFilteredWords().length === 0
                    ? 'No Words Available'
                    : 'Launch Game'}
              </button>
            </div>
          </div>
        )}

        {gameSetupMode && showPerformanceStats && (
          <div className="gre-words-challenge-performance">
            <div className="gre-words-challenge-performance-header">
              <h3>
                <FiBarChart size={20} />
                Performance Statistics
              </h3>
              <button
                onClick={() => setShowPerformanceStats(false)}
                className="gre-words-challenge-back-btn"
              >
                <FiArrowRight
                  size={16}
                  style={{ transform: 'rotate(180deg)' }}
                />
                Back to Setup
              </button>
            </div>

            {(() => {
              const gameData = getGameData();
              const totalStats = gameData.totalStats;
              const recentGames = gameData.gameHistory.slice(0, 10);
              const wordHistory = gameData.wordHistory;

              // Get words that need practice (accuracy < 70%)
              const wordsNeedingPractice = Object.entries(wordHistory)
                .filter(([_, history]) => {
                  const accuracy =
                    history.attempts > 0
                      ? history.correct / history.attempts
                      : 0;
                  return accuracy < 0.7 && history.attempts > 0;
                })
                .sort(
                  (a, b) =>
                    a[1].correct / a[1].attempts - b[1].correct / b[1].attempts
                )
                .slice(0, 10);

              return (
                <div className="gre-words-challenge-performance-content">
                  {/* Overall Stats */}
                  <div className="gre-words-challenge-performance-section">
                    <h4>
                      <FiTrendingUp size={16} />
                      Overall Performance
                    </h4>
                    <div className="gre-words-challenge-performance-stats">
                      <div className="gre-words-challenge-performance-stat">
                        <span className="gre-words-challenge-performance-stat-value">
                          {totalStats.gamesPlayed}
                        </span>
                        <span className="gre-words-challenge-performance-stat-label">
                          Games Played
                        </span>
                      </div>
                      <div className="gre-words-challenge-performance-stat">
                        <span className="gre-words-challenge-performance-stat-value">
                          {totalStats.totalWords}
                        </span>
                        <span className="gre-words-challenge-performance-stat-label">
                          Total Words
                        </span>
                      </div>
                      <div className="gre-words-challenge-performance-stat">
                        <span className="gre-words-challenge-performance-stat-value">
                          {totalStats.averageAccuracy}%
                        </span>
                        <span className="gre-words-challenge-performance-stat-label">
                          Average Accuracy
                        </span>
                      </div>
                      <div className="gre-words-challenge-performance-stat">
                        <span className="gre-words-challenge-performance-stat-value">
                          {totalStats.bestStreak}
                        </span>
                        <span className="gre-words-challenge-performance-stat-label">
                          Best Streak
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Recent Games */}
                  {recentGames.length > 0 && (
                    <div className="gre-words-challenge-performance-section">
                      <h4>
                        <FiCalendar size={16} />
                        Recent Games
                      </h4>
                      <div className="gre-words-challenge-performance-games">
                        {recentGames.map((game, index) => (
                          <div
                            key={index}
                            className={`gre-words-challenge-performance-game ${game.isCompleted === false ? 'incomplete' : ''}`}
                          >
                            <div className="gre-words-challenge-performance-game-header">
                              <span className="gre-words-challenge-performance-game-mode">
                                {game.gameMode === 'riddle'
                                  ? 'Riddle'
                                  : game.gameMode === 'word-to-definition'
                                    ? 'Word → Definition'
                                    : game.gameMode === 'mixed'
                                      ? 'Mixed'
                                      : 'Definition → Word'}
                                {game.isCompleted === false && (
                                  <span className="gre-words-challenge-incomplete-badge">
                                    Incomplete
                                  </span>
                                )}
                              </span>
                              <span className="gre-words-challenge-performance-game-date">
                                {new Date(game.timestamp).toLocaleDateString()}
                              </span>
                            </div>
                            <div className="gre-words-challenge-performance-game-stats">
                              <span>
                                {game.correct}/{game.wordsPlayed} correct
                                {game.isCompleted === false && (
                                  <span className="gre-words-challenge-target-info">
                                    {' '}
                                    (of {game.targetWordCount} target)
                                  </span>
                                )}
                              </span>
                              <span>{game.accuracy}% accuracy</span>
                              <span>Streak: {game.bestStreak}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Words Needing Practice */}
                  {wordsNeedingPractice.length > 0 && (
                    <div className="gre-words-challenge-performance-section">
                      <h4>
                        <FiTarget size={16} />
                        Words Needing Practice
                      </h4>
                      <div className="gre-words-challenge-performance-words">
                        {wordsNeedingPractice.map(([wordKey, history]) => {
                          const accuracy = Math.round(
                            (history.correct / history.attempts) * 100
                          );
                          return (
                            <div
                              key={wordKey}
                              className="gre-words-challenge-performance-word"
                            >
                              <div className="gre-words-challenge-performance-word-info">
                                <span className="gre-words-challenge-performance-word-text">
                                  {history.word}
                                </span>
                                <span className="gre-words-challenge-performance-word-difficulty">
                                  {history.difficulty}
                                </span>
                              </div>
                              <div className="gre-words-challenge-performance-word-stats">
                                <span>
                                  {history.correct}/{history.attempts}
                                </span>
                                <span
                                  className={`gre-words-challenge-performance-word-accuracy ${accuracy < 50 ? 'low' : accuracy < 70 ? 'medium' : 'high'}`}
                                >
                                  {accuracy}%
                                </span>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {totalStats.gamesPlayed === 0 && (
                    <div className="gre-words-challenge-performance-empty">
                      <FiTarget size={48} />
                      <h4>No Games Played Yet</h4>
                      <p>Start playing to see your performance statistics!</p>
                    </div>
                  )}
                </div>
              );
            })()}
          </div>
        )}

        {!gameSetupMode && !gameCompleted && currentWord && (
          <div className="gre-words-challenge-game">
            {/* Progress Display */}
            {showTextProgress ? (
              <div className="gre-words-challenge-game-progress">
                <div className="gre-words-challenge-progress-info">
                  <span>
                    Question {wordsPlayed} of {targetWordCount}
                  </span>
                  <span>Score: {gameScore}</span>
                  {gameStats.streak > 0 && (
                    <span className="gre-words-challenge-streak-indicator">
                      <FiZap size={14} /> {gameStats.streak} streak
                    </span>
                  )}
                </div>
              </div>
            ) : (
              <div className="gre-words-challenge-game-progress">
                <div className="gre-words-challenge-progress-info">
                  <span>
                    Question {wordsPlayed} of {targetWordCount}
                  </span>
                  <span>Score: {gameScore}</span>
                  {gameStats.streak > 0 && (
                    <span className="gre-words-challenge-streak-indicator">
                      <FiZap size={14} /> {gameStats.streak} streak
                    </span>
                  )}
                </div>
                <div className="gre-words-challenge-progress-bar">
                  <div
                    className="gre-words-challenge-progress-fill"
                    style={{
                      width: `${(wordsPlayed / targetWordCount) * 100}%`,
                    }}
                  ></div>
                </div>
              </div>
            )}

            {/* Timer */}
            {isTimedMode && timeLeft > 0 && (
              <div className="gre-words-challenge-timer-display">
                <FiClock size={16} />
                <div
                  className="gre-words-challenge-timer-bar"
                  style={{
                    width: `${(timeLeft / maxTime) * 85}%`,
                    backgroundColor:
                      timeLeft <= 5
                        ? '#ef4444'
                        : timeLeft <= 10
                          ? '#f59e0b'
                          : '#10b981',
                  }}
                ></div>
                <span className="gre-words-challenge-timer-text">
                  {timeLeft}s
                </span>
              </div>
            )}

            {/* Question */}
            <div className="gre-words-challenge-question-container">
              <div className="gre-words-challenge-question-header">
                <h3 className="gre-words-challenge-question-text">
                  {currentWord.question}
                </h3>
                {currentWord.difficulty && (
                  <div className="gre-words-challenge-difficulty-indicator">
                    <span
                      className={`gre-words-challenge-difficulty-badge gre-words-challenge-difficulty-${(currentWord.difficulty || '').toLowerCase()}`}
                    >
                      {(currentWord.difficulty || '').toUpperCase()}
                    </span>
                  </div>
                )}
              </div>

              {gameMode === 'mixed' && currentWord.roundMode && (
                <div className="gre-words-challenge-round-pill">
                  This round: {roundModeLabel(currentWord.roundMode)}
                </div>
              )}

              {firstLetterHintText && (
                <p className="gre-words-challenge-first-letter-hint">
                  Starts with: <strong>{firstLetterHintText}</strong>
                </p>
              )}

              <div className="gre-words-challenge-question-actions">
                {currentWord.roundMode === 'word-to-definition' && (
                  <button
                    type="button"
                    onClick={() => speakWord(currentWord.word)}
                    className="gre-words-challenge-pronunciation-btn"
                    title="Hear pronunciation"
                  >
                    <FiVolume2 size={16} />
                  </button>
                )}
                {(currentWord.roundMode === 'riddle' ||
                  currentWord.roundMode === 'definition-to-word') && (
                  <button
                    type="button"
                    onClick={() =>
                      speakWord(currentWord.question, { rate: 0.9 })
                    }
                    className="gre-words-challenge-pronunciation-btn"
                    title="Read question aloud"
                  >
                    <FiVolume2 size={16} />
                  </button>
                )}
              </div>
            </div>

            {!showResult &&
              currentWord.options &&
              currentWord.options.length > 0 && (
                <div className="gre-words-challenge-hints-row">
                  <button
                    type="button"
                    className="gre-words-challenge-hint-btn"
                    onClick={applyFiftyFifty}
                    disabled={hint5050Used || currentWord.options.length < 4}
                    title="Remove two incorrect options"
                  >
                    <FiHelpCircle size={14} /> 50/50
                  </button>
                  <button
                    type="button"
                    className="gre-words-challenge-hint-btn"
                    onClick={applyFirstLetterHint}
                    disabled={hintLetterUsed}
                    title="Reveal first character of the answer"
                  >
                    <FiHelpCircle size={14} /> First letter
                  </button>
                  <span className="gre-words-challenge-keyboard-hint">
                    Keys 1–{Math.min(4, displayOptions.length)} select
                  </span>
                </div>
              )}

            {/* Answer Options */}
            <div className="gre-words-challenge-options-container">
              {displayOptions.map((option, index) => (
                <button
                  key={`${String(option)}-${index}`}
                  onClick={() => handleAnswerSelect(option)}
                  disabled={showResult}
                  className={`gre-words-challenge-option-btn ${
                    showResult
                      ? option === currentWord.correctAnswer
                        ? 'correct'
                        : option === selectedAnswer
                          ? 'incorrect'
                          : 'disabled'
                      : ''
                  }`}
                >
                  <span className="gre-words-challenge-option-label">
                    <span className="gre-words-challenge-option-index">
                      {index + 1}.
                    </span>
                    <span className="gre-words-challenge-option-text">
                      {option}
                    </span>
                  </span>
                  {showResult && option === currentWord.correctAnswer && (
                    <FiCheckCircle
                      size={16}
                      className="gre-words-challenge-option-icon"
                    />
                  )}
                  {showResult &&
                    option === selectedAnswer &&
                    option !== currentWord.correctAnswer && (
                      <FiXCircle
                        size={16}
                        className="gre-words-challenge-option-icon"
                      />
                    )}
                </button>
              ))}
            </div>

            {/* Result Feedback */}
            {showResult && (
              <div
                className={`gre-words-challenge-result-feedback ${selectedAnswer === currentWord.correctAnswer ? 'correct' : 'incorrect'}`}
              >
                {selectedAnswer === 'TIME_UP' ? (
                  <p>
                    ⏰ Time's up! The correct answer was:{' '}
                    <strong>{currentWord.correctAnswer}</strong>
                  </p>
                ) : selectedAnswer === currentWord.correctAnswer ? (
                  <p>✅ Correct! Well done!</p>
                ) : (
                  <p>
                    ❌ Incorrect. The correct answer was:{' '}
                    <strong>{currentWord.correctAnswer}</strong>
                  </p>
                )}

                {/* Word explanation */}
                <div className="gre-words-challenge-word-explanation">
                  <div className="gre-words-challenge-word-title">
                    <strong>{currentWord.word}</strong>
                  </div>

                  {/* English Definition */}
                  <div className="gre-words-challenge-definition-section">
                    <div className="gre-words-challenge-definition-header">
                      <span className="gre-words-challenge-language-label">
                        🇺🇸 English
                      </span>
                    </div>
                    <p className="gre-words-challenge-definition-text">
                      {currentWord.definition_en || currentWord.definition}
                    </p>
                    {(currentWord.example_en || currentWord.example) && (
                      <p className="gre-words-challenge-example-sentence">
                        <em>
                          Example:{' '}
                          {currentWord.example_en || currentWord.example}
                        </em>
                      </p>
                    )}
                  </div>

                  {/* French Definition */}
                  <div className="gre-words-challenge-definition-section">
                    <div className="gre-words-challenge-definition-header">
                      <span className="gre-words-challenge-language-label">
                        🇫🇷 Français
                      </span>
                    </div>
                    <p className="gre-words-challenge-definition-text">
                      {currentWord.definition_fr ||
                        currentWord.definition_en ||
                        currentWord.definition}
                    </p>
                    {(currentWord.example_fr ||
                      currentWord.example_en ||
                      currentWord.example) && (
                      <p className="gre-words-challenge-example-sentence">
                        <em>
                          Exemple:{' '}
                          {currentWord.example_fr ||
                            currentWord.example_en ||
                            currentWord.example}
                        </em>
                      </p>
                    )}
                  </div>

                  {(() => {
                    const nuance =
                      currentWord.usage_nuance_fr ||
                      currentWord.usage_nuance_en
                        ? {
                            usage_nuance_fr: currentWord.usage_nuance_fr || '',
                            usage_nuance_en: currentWord.usage_nuance_en || '',
                            isFalseFriend: /faux ami|false friend/i.test(
                              `${currentWord.usage_nuance_fr || ''}\n${currentWord.usage_nuance_en || ''}`
                            ),
                          }
                        : getNuanceForWord(currentWord.word);

                    if (!nuance) return null;
                    const fr = (nuance.usage_nuance_fr || '').trim();
                    const en = (nuance.usage_nuance_en || '').trim();
                    const displayBlocks = [
                      (nuanceLanguage === 'both' || nuanceLanguage === 'en') &&
                        en && {
                          key: 'en',
                          label: 'EN',
                          text: en,
                        },
                      (nuanceLanguage === 'both' || nuanceLanguage === 'fr') &&
                        fr && {
                          key: 'fr',
                          label: 'FR',
                          text: fr,
                        },
                    ].filter(Boolean);

                    if (displayBlocks.length === 0) {
                      const fallback = en || fr;
                      if (!fallback) return null;
                      displayBlocks.push({
                        key: en ? 'en' : 'fr',
                        label: en ? 'EN' : 'FR',
                        text: fallback,
                      });
                    }

                    return (
                      <div
                        className={`gre-words-challenge-nuance-callout ${nuance.isFalseFriend ? 'faux-ami' : ''}`}
                      >
                        <div className="gre-words-challenge-nuance-header">
                          <div className="gre-words-challenge-nuance-title">
                            <span className="gre-words-challenge-nuance-icon">
                              {nuance.isFalseFriend ? '⚠️' : '💡'}
                            </span>
                            <span>
                              {nuance.isFalseFriend
                                ? 'False friend / faux ami'
                                : 'Usage nuance / nuance d’usage'}
                            </span>
                          </div>
                          {en && fr && (
                            <div
                              className="gre-words-challenge-nuance-toggle"
                              role="group"
                              aria-label="Nuance language"
                            >
                              {['both', 'en', 'fr'].map((language) => (
                                <button
                                  key={language}
                                  type="button"
                                  className={
                                    nuanceLanguage === language ? 'active' : ''
                                  }
                                  onClick={() =>
                                    handleNuanceLanguageChange(language)
                                  }
                                >
                                  {language === 'both'
                                    ? 'Both'
                                    : language.toUpperCase()}
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                        <div className="gre-words-challenge-nuance-body">
                          {displayBlocks.map((block) => (
                            <div
                              className="gre-words-challenge-nuance-language-block"
                              key={block.key}
                            >
                              <span className="gre-words-challenge-nuance-language-label">
                                {block.label}
                              </span>
                              <div>{renderNuanceInlineMarkdown(block.text)}</div>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })()}
                </div>
              </div>
            )}

            {/* Game Controls */}
            <div className="gre-words-challenge-game-controls">
              <button
                onClick={exitGame}
                className="gre-words-challenge-exit-btn"
              >
                Exit Game
              </button>

              {showResult && (
                <button
                  onClick={advanceToNextQuestion}
                  className="gre-words-challenge-next-question-btn"
                >
                  Go to next question
                  <FiArrowRight size={16} />
                </button>
              )}
            </div>
          </div>
        )}

        {gameCompleted && (
          <div className="gre-words-challenge-game-completion">
            <div className="gre-words-challenge-completion-header">
              <FiAward
                size={48}
                className="gre-words-challenge-completion-icon"
              />
              <h2 className="gre-words-challenge-completion-title">
                Game Complete!
              </h2>
              <p className="gre-words-challenge-completion-subtitle">
                You've completed {wordsPlayed} words
                {wordsPlayed < targetWordCount && (
                  <span className="gre-words-challenge-early-exit-note">
                    {' '}
                    (Target: {targetWordCount})
                  </span>
                )}
              </p>
            </div>

            <div className="gre-words-challenge-final-stats">
              <div className="gre-words-challenge-final-stat-item">
                <div className="gre-words-challenge-final-stat-icon">
                  <FiTarget size={24} />
                </div>
                <div className="gre-words-challenge-final-stat-content">
                  <span className="gre-words-challenge-final-stat-value">
                    {gameStats.correct + gameStats.incorrect > 0
                      ? Math.round(
                          (gameStats.correct /
                            (gameStats.correct + gameStats.incorrect)) *
                            100
                        )
                      : 0}
                    %
                  </span>
                  <span className="gre-words-challenge-final-stat-label">
                    Accuracy
                  </span>
                </div>
              </div>

              <div className="gre-words-challenge-final-stat-item">
                <div className="gre-words-challenge-final-stat-icon">
                  <FiZap size={24} />
                </div>
                <div className="gre-words-challenge-final-stat-content">
                  <span className="gre-words-challenge-final-stat-value">
                    {gameStats.bestStreak}
                  </span>
                  <span className="gre-words-challenge-final-stat-label">
                    Best Streak
                  </span>
                </div>
              </div>

              <div className="gre-words-challenge-final-stat-item">
                <div className="gre-words-challenge-final-stat-icon">
                  <FiAward size={24} />
                </div>
                <div className="gre-words-challenge-final-stat-content">
                  <span className="gre-words-challenge-final-stat-value">
                    {gameScore}
                  </span>
                  <span className="gre-words-challenge-final-stat-label">
                    Final Score
                  </span>
                </div>
              </div>
            </div>

            {sessionMissedWords.length > 0 && (
              <div className="gre-words-challenge-missed-review">
                <h3 className="gre-words-challenge-missed-title">
                  <FiTarget size={18} /> Words to review
                </h3>
                <p className="gre-words-challenge-missed-sub">
                  You missed {sessionMissedWords.length} in this session. Try
                  them again in flashcards or the word list.
                </p>
                <ul className="gre-words-challenge-missed-list">
                  {sessionMissedWords.map((w) => (
                    <li key={w}>{w}</li>
                  ))}
                </ul>
              </div>
            )}

            {/* Performance Overview */}
            {(() => {
              const gameData = getGameData();
              const totalStats = gameData.totalStats;
              const recentGames = gameData.gameHistory.slice(0, 5); // Show last 5 games

              return (
                <div className="gre-words-challenge-completion-performance">
                  <h3 className="gre-words-challenge-completion-performance-title">
                    <FiTrendingUp size={20} />
                    Your Progress
                  </h3>

                  {/* Overall Stats Summary */}
                  <div className="gre-words-challenge-completion-overall-stats">
                    <div className="gre-words-challenge-completion-stat">
                      <span className="gre-words-challenge-completion-stat-value">
                        {totalStats.gamesPlayed}
                      </span>
                      <span className="gre-words-challenge-completion-stat-label">
                        Total Games
                      </span>
                    </div>
                    <div className="gre-words-challenge-completion-stat">
                      <span className="gre-words-challenge-completion-stat-value">
                        {totalStats.averageAccuracy}%
                      </span>
                      <span className="gre-words-challenge-completion-stat-label">
                        Avg Accuracy
                      </span>
                    </div>
                    <div className="gre-words-challenge-completion-stat">
                      <span className="gre-words-challenge-completion-stat-value">
                        {totalStats.bestStreak}
                      </span>
                      <span className="gre-words-challenge-completion-stat-label">
                        Best Streak
                      </span>
                    </div>
                    <div className="gre-words-challenge-completion-stat">
                      <span className="gre-words-challenge-completion-stat-value">
                        {totalStats.totalWords}
                      </span>
                      <span className="gre-words-challenge-completion-stat-label">
                        Words Played
                      </span>
                    </div>
                  </div>

                  {/* Recent Games */}
                  {recentGames.length > 1 && (
                    <div className="gre-words-challenge-completion-recent">
                      <h4 className="gre-words-challenge-completion-recent-title">
                        Recent Performance
                      </h4>
                      <div className="gre-words-challenge-completion-recent-games">
                        {recentGames.slice(0, 4).map((game, index) => (
                          <div
                            key={index}
                            className="gre-words-challenge-completion-recent-game"
                          >
                            <div className="gre-words-challenge-completion-recent-game-accuracy">
                              {game.accuracy}%
                            </div>
                            <div className="gre-words-challenge-completion-recent-game-info">
                              <span className="gre-words-challenge-completion-recent-game-mode">
                                {game.gameMode === 'riddle'
                                  ? 'Riddle'
                                  : game.gameMode === 'word-to-definition'
                                    ? 'W→D'
                                    : game.gameMode === 'mixed'
                                      ? 'Mix'
                                      : 'D→W'}
                              </span>
                              <span className="gre-words-challenge-completion-recent-game-words">
                                {game.wordsPlayed}w
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Performance Trend */}
                  {recentGames.length > 2 && (
                    <div className="gre-words-challenge-completion-trend">
                      {(() => {
                        const lastThreeGames = recentGames.slice(0, 3);
                        const avgAccuracy = Math.round(
                          lastThreeGames.reduce(
                            (sum, game) => sum + game.accuracy,
                            0
                          ) / lastThreeGames.length
                        );
                        const trend =
                          lastThreeGames[0].accuracy >
                          lastThreeGames[2].accuracy
                            ? 'up'
                            : lastThreeGames[0].accuracy <
                                lastThreeGames[2].accuracy
                              ? 'down'
                              : 'stable';

                        return (
                          <div
                            className={`gre-words-challenge-completion-trend-indicator ${trend}`}
                          >
                            {trend === 'up' && <FiTrendingUp size={16} />}
                            {trend === 'down' && <FiTarget size={16} />}
                            {trend === 'stable' && <FiTarget size={16} />}
                            <span>
                              {trend === 'up' && 'Improving! '}
                              {trend === 'down' && 'Keep practicing! '}
                              {trend === 'stable' && 'Consistent performance! '}
                              Last 3 games: {avgAccuracy}% avg
                            </span>
                          </div>
                        );
                      })()}
                    </div>
                  )}
                </div>
              );
            })()}

            <div className="gre-words-challenge-completion-actions">
              <button
                onClick={() => setShowPerformanceStats(true)}
                className="gre-words-challenge-view-full-stats-btn"
              >
                <FiBarChart size={16} />
                View Full Stats
              </button>
              <button
                onClick={resetGame}
                className="gre-words-challenge-play-again-btn"
              >
                <FiRotateCcw size={16} />
                Play Again
              </button>
              <button
                onClick={closeGame}
                className="gre-words-challenge-close-game-btn"
              >
                Close
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default GREWordsChallenge;
